import { Directive, ElementRef, Input, Type } from '@angular/core';
import { exhaustMap, tap } from 'rxjs/operators';
import {
  ApFoxitLoadedDirective, FoxitConfig, FoxitConfigServiceToken, FoxitModule,
  FoxitStyleServiceToken,
} from 'webcommon/lazy-load-commonjs/foxit';
import { ApBaseClass } from 'webcommon/shared';
import { StyleService } from '../../services';
import { FoxitConfigService2 } from './FoxitConfigService2';
import { FoxitSignPdfRenderer } from './FoxitSignPdfRenderer';
import { FoxitViewRenderer } from './FoxitViewRenderer';
import { PdfRendererFactory } from './PdfRendererFactory';

export type apFoxitRendererNames = 'view' | 'sign';

export type _apFoxitRendererClassByName = {
  'view': FoxitViewRenderer;
  'sign': FoxitSignPdfRenderer;
};

const RendererClassByName = new Map<apFoxitRendererNames, Type<_apFoxitRendererClassByName[apFoxitRendererNames]>>([
  ['view', FoxitViewRenderer],
  ['sign', FoxitSignPdfRenderer],
]);


@Directive({
  selector: '[apFoxitViewer]',
  providers: [
    {
      provide: FoxitConfigServiceToken,
      useExisting: FoxitConfigService2,
    },
    {
      provide: FoxitStyleServiceToken,
      useExisting: StyleService,
    },
  ],
  hostDirectives: [
    {
      directive: ApFoxitLoadedDirective,
    },
  ],
  standalone: true,
})
export class ApFoxitViewerDirective<TRendererName extends apFoxitRendererNames> extends ApBaseClass {
  readonly deferredPdfRenderer = this.pdfRendererFactory.createDeferred<_apFoxitRendererClassByName[TRendererName]>();

  @Input() rendererName: TRendererName;

  @Input() afterCreate: (
    pdfRenderer: _apFoxitRendererClassByName[TRendererName],
  ) => void = () => {
    // by default, noop
  };

  @Input() set data64(value: string) {
    this.openPDFData(value);
  }

  async openPDFData(base64String: string): Promise<void> {
    const pdfRenderer = await this.deferredPdfRenderer.resultPromise;
    await pdfRenderer.openPDFData(base64String);
  }

  @Input() set dataBlob(value: Blob) {
    this.openPDFDataBlob(value);
  }

  async openPDFDataBlob(blob: Blob): Promise<void> {
    const pdfRenderer = await this.deferredPdfRenderer.resultPromise;
    await pdfRenderer.openPDFDataBlob(blob);
  }

  // this HostListener wasn't working, bc of timing; that's why we are subscribing below instead
  // @HostListener('apFoxitLoaded', ['$event'])
  protected afterLoad(
    value: {
      foxitModule: FoxitModule;
      foxitConfig: FoxitConfig;
    },
  ): void {
    const rendererClass = RendererClassByName.get(this.rendererName) as Type<_apFoxitRendererClassByName[TRendererName]> | undefined;
    if (!rendererClass) {
      throw new Error(`Invalid rendererName: '${this.rendererName}'`);
    }
    const pdfRenderer = this.deferredPdfRenderer.init(
      value.foxitConfig,
      value.foxitModule,
      this.elementRef.nativeElement,
      rendererClass,
    );
    this.afterCreate(pdfRenderer);
    this.onDestroy$.pipe(
      exhaustMap(async () => await pdfRenderer.ngOnDestroy()),
    ).subscribe();
  };

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private pdfRendererFactory: PdfRendererFactory,
    private apFoxitLoadedDirective: ApFoxitLoadedDirective,
  ) {
    super();
    // move this call to ngOnInit, if it's happening too early for afterCreate
    this.apFoxitLoadedDirective.apFoxitLoaded.pipe(
      tap(value => this.afterLoad(value)),
      this.takeUntilDestroy(),
    ).subscribe();
  }
}

